import type { Id } from '@contracts/common';
import type { SharingDTO } from '@contracts/sharing';

/**
 * Who already sees a home, as a person picker needs it (sharing spec §8.3, §9, L5-6): its live members, and whether it
 * is a project; `'public'` when everybody does. A pick outside it is added by the server on save, so the picker says so.
 */
export type Audience = { members: ReadonlySet<Id>; project: boolean } | 'public';

export const WILL_ADD = 'sẽ được thêm vào chia sẻ';
export const WILL_ADD_PROJECT = 'sẽ được thêm vào chia sẻ của dự án';

/** The audience a read of `GET …/sharing` describes; `undefined` while it is not read (then nobody is marked). */
export function audienceOf(sharing: SharingDTO | undefined): Audience | undefined {
  if (sharing === undefined) return undefined;
  if (sharing.mode === 'public') return 'public';
  return {
    members: new Set([sharing.owner.id, ...sharing.members.map((member) => member.id)]),
    project: sharing.home.kind === 'project',
  };
}

/** A draft's audience (a create form): its owner and the people chosen, or everybody when public. */
export function draftAudience(mode: SharingDTO['mode'], ownerId: Id, memberIds: readonly Id[], project = false): Audience {
  if (mode === 'public') return 'public';
  return { members: new Set([ownerId, ...(mode === 'shared' ? memberIds : [])]), project };
}

/** The words after a person's name, or `null` when they already see the home (or nobody is to be marked). */
export function willAddWords(audience: Audience | undefined, userId: Id): string | null {
  if (audience === undefined || audience === 'public' || audience.members.has(userId)) return null;
  return audience.project ? WILL_ADD_PROJECT : WILL_ADD;
}

/** A person's name in a picker row, with the mark under it when the save will add them. */
export function PersonLabel({ name, audience, userId }: { name: string; audience: Audience | undefined; userId: Id }) {
  const words = willAddWords(audience, userId);
  if (words === null) return <>{name}</>;
  return (
    <>
      {name}
      <span className="block text-xs font-normal text-[hsl(var(--h-muted-foreground))]">{words}</span>
    </>
  );
}
